import React, { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import RightSlidingPanel from "./RightSlidingPanel";
import { spacesList } from "../../../Constants";
import SpaceGridItem from "../../../Components/SpaceGridItem";
import { Interface } from "readline";
import {
  faPen,
  faSpinner,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { useDispatch, useSelector } from "react-redux";
import { getVenueSpaces } from "../../../Services/Store/Reducers/SpaceReducers";
import { adjustVenueSpaceCount } from "../../../Services/Store/Reducers/VanueReducers";
import {
  deleteSpaceAction,
  getVenueSpacesAction,
} from "../../../Services/Store/Actions/SpaceActions";
import { showToast } from "../../../utils/helpers";
import AddVanueSpace from "../../../Components/Modals/AddVanueSpace";
import { VenueTypes } from "../../../Constants/Interfaces";
import ConfirmationModal from "../../../Components/ConfirmationModal";

interface VenuesSpaceListType {
  show: boolean;
  addNew?: boolean;
  venue: VenueTypes;
  onClose: Function;
  onSpacesChanged?: Function;
}

const VenuesSpaceList: React.FC<VenuesSpaceListType> = ({
  show,
  addNew = true,
  venue,
  onClose,
  onSpacesChanged,
}) => {
  const dispatch = useDispatch();

  const { venueSpaces } = useSelector((state: any) => state.spaces);
  const { user } = useSelector((state: any) => state.auth);

  // Owner-only actions (Add / Edit / Delete space) may only be used when the
  // venue belongs to the logged-in user. An admin browsing another user's
  // venue gets a read-only spaces list.
  const isOwner = !!user?.id && (venue as any)?.userId === user.id;
  const canManageSpaces = addNew && isOwner;

  const [loading, setLoading] = useState(false);
  const [spaces, setSpaces] = useState([]);
  const [pagging, setPagging] = useState({});
  const [venueId, setVenueId] = useState("");

  const [addSpace, setAddVenueSpace] = useState({ show: false, item: {} });

  const [deletingSpace, setDeletingSpace] = useState<Record<string, boolean>>(
    {}
  );
  const [deleteConfirm, setDeleteConfirm] = useState<{
    show: boolean;
    item: any;
  }>({ show: false, item: null });

  const deleteSpace = (item: { id?: string; name?: string }) => {
    const spaceId = item?.id;
    if (!spaceId || deletingSpace[spaceId]) {
      return;
    }

    setDeletingSpace((prev) => ({ ...prev, [spaceId]: true }));

    deleteSpaceAction({ id: spaceId }, dispatch)
      .then((res) => {
        if (res.type === "success") {
          // Silently decrement this venue's space count in the lists instead
          // of reloading the whole venue list.
          dispatch(
            adjustVenueSpaceCount({ venueId: (venue as any)?.id, delta: -1 })
          );
          showToast("success", res.message || "Space deleted successfully.");
        } else {
          showToast(res.type, res.message);
        }
      })
      .catch((err) => {
        showToast("error", err.message);
      })
      .finally(() =>
        setDeletingSpace((prev) => ({ ...prev, [spaceId]: false }))
      );
  };

  const getSpaces = (page = "1") => {
    if (!loading) {
      setLoading(true);
      getVenueSpacesAction({ venueId: venue.id, page }, dispatch)
        .then((res) => {
          if (res.type === "success") {
            setPagging(res.pagination);
          }
        })
        .catch((err) => {
          showToast("error", err.message);
        })
        .finally(() => setLoading(false));
    }
  };

  const resetspaceList = () => {
    dispatch(getVenueSpaces({ items: [], updateList: true }));
  };

  useEffect(() => {
    // Just mirror the store list locally. We do NOT notify the parent here:
    // diffing the count also fired on the initial load, which reloaded the
    // whole venue list every time the panel opened. Add/delete now update the
    // venue's space count in the store silently (see deleteSpace / onSpaceSaved).
    setSpaces(venueSpaces);
  }, [venueSpaces]);

  useEffect(() => {
    getSpaces();

    setVenueId(venue.id);

    return resetspaceList;
  }, []);

  return (
    <RightSlidingPanel
      show={show}
      align="right"
      title="Spaces"
      onClosePanel={() => onClose(false)}
      addNewSpace={
        canManageSpaces
          ? () => setAddVenueSpace({ show: true, item: {} })
          : null
      }
    >
      <div className="row">
        {!loading && spaces.length === 0 && (
          <div className="no-result-wrapper">
            <p>No Spaces Available</p>
          </div>
        )}

        {spaces.length > 0 &&
          spaces.map((item, index) => (
            <div
              key={`right-panel-space-${index}`}
              className="col-md-6 col-sm-6 col-xs-12"
            >
              <div className="space-card-wrapper position-relative">
                <SpaceGridItem
                  item={item}
                  onClick={() => setAddVenueSpace({ show: true, item })}
                />

                {String(item?.status).toLowerCase() === "draft" && (
                  <span className="space-status-pill">Draft</span>
                )}

                {canManageSpaces && item?.id && (
                  <div className="space-card-actions">
                    <span
                      className="space-action-btn space-edit-btn as-link"
                      title="Edit space"
                      onClick={(e) => {
                        e.stopPropagation();
                        setAddVenueSpace({ show: true, item });
                      }}
                    >
                      <FontAwesomeIcon icon={faPen} />
                    </span>

                    <span
                      className="space-action-btn space-delete-btn as-link"
                      title="Delete space"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteConfirm({ show: true, item });
                      }}
                    >
                      {deletingSpace[item.id] ? (
                        <FontAwesomeIcon icon={faSpinner} spin />
                      ) : (
                        <FontAwesomeIcon icon={faTrash} />
                      )}
                    </span>
                  </div>
                )}
              </div>
            </div>
          ))}

        {loading && (
          <div className="text-center">
            <FontAwesomeIcon icon={faSpinner} spin />
          </div>
        )}
      </div>

      {addSpace && addSpace.show && (
        <AddVanueSpace
          showActionBtn={canManageSpaces}
          spaceItem={addSpace.item}
          venueId={venueId}
          show={addSpace.show}
          closeModal={() => setAddVenueSpace({ show: false, item: {} })}
          onSpaceCreated={() =>
            dispatch(
              adjustVenueSpaceCount({ venueId: (venue as any)?.id, delta: 1 })
            )
          }
        />
      )}

      {deleteConfirm.show && (
        <ConfirmationModal
          show={deleteConfirm.show}
          heading="Delete Space"
          message={`Are you sure you want to delete the space ${
            deleteConfirm?.item?.name || ""
          }?`}
          closeModal={() => setDeleteConfirm({ show: false, item: null })}
          buttons={[
            {
              text: "Yes",
              action: () => {
                deleteSpace(deleteConfirm.item);
                setDeleteConfirm({ show: false, item: null });
              },
            },
            {
              text: "Cancel",
              variant: "outline-dark",
              className: "confirmation-cancel-btn",
              action: () => setDeleteConfirm({ show: false, item: null }),
            },
          ]}
        />
      )}
    </RightSlidingPanel>
  );
};

export default VenuesSpaceList;
